// © 2026 Buddy Media LLC. All rights reserved.
//
// T-678 Phase 3 — repointed from BW backend (/api/admin/q-stream/*) to
// MCP backend (/admin/api/q-stream/*) per the umbrella plan. This is
// the first page repoint after T-679 shipped the MCP-side endpoints
// (commit 1b3716f, deployed 2026-05-09T16:36Z).
//
// Bearer model: cluster-mesh JWT (T-612) for worker callers + magic-
// link admin JWT for Marc's interactive sessions. mcpFetch handles
// the same auth flow as apiFetch but targets dev-mcp.buddymedia.online.

import { useQuery } from "@tanstack/react-query";
import { mcpFetch } from "@/lib/api";

interface ClusterPulse {
  workers?: unknown;
  recent?: unknown;
  [k: string]: unknown;
}

export function ClusterPulsePage() {
  const q = useQuery({
    queryKey: ["cluster-pulse-smoke"],
    queryFn: () => mcpFetch<ClusterPulse>("/admin/api/q-stream/cluster-pulse"),
    refetchInterval: 30_000,
  });

  return (
    <div className="space-y-4">
      <header className="flex items-baseline justify-between gap-2">
        <h1
          className="bg-clip-text text-2xl font-bold text-transparent"
          style={{
            backgroundImage:
              "linear-gradient(135deg, #ffffff 0%, #c7d2fe 60%, var(--brand-accent) 100%)",
          }}
        >
          Cluster pulse
        </h1>
        <span className="text-sm text-slate-400">
          {q.isFetching
            ? "Refreshing…"
            : q.dataUpdatedAt
              ? `Last refreshed ${Math.round(
                  (Date.now() - q.dataUpdatedAt) / 1000,
                )}s ago`
              : "—"}
        </span>
      </header>

      <p className="text-sm text-slate-400">
        Phase 1 smoke surface. If this card renders the full payload below,
        the new SPA's cross-origin auth + CORS + React Query stack is
        working end-to-end and we're cleared to migrate the real pages.
      </p>

      <section
        className="rounded-xl border p-4"
        style={{
          borderColor: "var(--bw-border)",
          background: "var(--bw-surface)",
        }}
      >
        {q.isLoading && (
          <p className="text-sm text-slate-400">Loading cluster pulse…</p>
        )}
        {q.isError && (
          <div>
            <p className="text-sm font-medium text-rose-200">
              Couldn't load cluster pulse
            </p>
            <p className="mt-1 text-sm text-rose-200/70">
              {(q.error as Error).message}
            </p>
            <p className="mt-2 text-xs text-rose-200/60">
              Likely causes: the backend is unreachable, your bearer
              expired, or CORS isn't allowing this origin yet (check
              CORS_ALLOW_ORIGINS in the backend env).
            </p>
          </div>
        )}
        {q.data && (
          <pre className="overflow-auto text-xs text-slate-300">
            {JSON.stringify(q.data, null, 2)}
          </pre>
        )}
      </section>
    </div>
  );
}
